/**
 * Reactive TanStack Solid-Query hooks over the mail REST layer (`api.ts`).
 *
 * Local-first posture: list/read/search queries keep a generous `staleTime` and
 * `placeholderData: keepPreviousData` so re-opening a folder is instant. Reads
 * flow through the **persistent offline mirror** (`mirror-service.ts`): each
 * successful fetch is written through to IndexedDB, and a fetch that fails
 * because we are offline falls back to the mirror — so a reload with no server
 * still shows folders/messages/threads/search, and compose ops queue offline
 * and replay on reconnect. Flag toggles are applied optimistically.
 */

import { createMutation, keepPreviousData, type UseMutationResult } from "@tanstack/solid-query";

import { queryClient } from "@/lib/query-client";
import { createSafeQuery } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import {
  type ConnectAccountInput,
  type FlagsInput,
  IMAP_FLAG_FLAGGED,
  IMAP_FLAG_SEEN,
  type MailAccountView,
  mailApi,
  mailKeys,
  type MailOAuthProvider,
  type MailOAuthStart,
  type MailRule,
  type MessageSummary,
  type MoveInput,
  type SendMessageInput,
  type SyncResult,
} from "./api";
import { mailMirror } from "./mirror-service";

// When the connection returns, replay any queued compose ops and refetch every
// mail query so the views reconcile with the server. Registered once, lazily.
mailMirror.ensureReconnectListener(() => {
  void queryClient.invalidateQueries({ queryKey: mailKeys.all });
});

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useMailAccounts() {
  return createSafeQuery(() => ({
    queryKey: mailKeys.accounts(),
    queryFn: () => mailMirror.accounts(),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  }));
}

export function useMailFolders(accountId: () => string | undefined) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.folders(accountId()),
    queryFn: () => mailMirror.folders(accountId() ?? ""),
    enabled: Boolean(accountId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  }));
}

export function useMailThreads(accountId: () => string | undefined, folder: () => string) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.threads(accountId(), folder()),
    queryFn: () => mailMirror.threads(accountId() ?? "", folder()),
    enabled: Boolean(accountId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  }));
}

export function useMailMessages(accountId: () => string | undefined, folder: () => string) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.messages(accountId(), folder()),
    queryFn: () => mailMirror.messages(accountId() ?? "", folder()),
    enabled: Boolean(accountId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  }));
}

export function useMailMessage(
  accountId: () => string | undefined,
  folder: () => string,
  uid: () => number | undefined,
) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.message(accountId(), folder(), uid()),
    queryFn: () => mailMirror.message(accountId() ?? "", folder(), uid() ?? 0),
    enabled: Boolean(accountId()) && uid() !== undefined,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  }));
}

export function useMailSearch(accountId: () => string | undefined, q: () => string) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.search(accountId(), q()),
    queryFn: () => mailMirror.search(accountId() ?? "", q()),
    enabled: Boolean(accountId()) && q().trim().length > 0,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  }));
}

export function useMailRules(accountId: () => string | undefined) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.rules(accountId()),
    queryFn: () => mailApi.getRules(accountId() ?? ""),
    enabled: Boolean(accountId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  }));
}

/** The account's access-audit trail (who used the mailbox, when, what for). */
export function useMailAudit(accountId: () => string | undefined) {
  return createSafeQuery(() => ({
    queryKey: mailKeys.audit(accountId()),
    queryFn: () => mailApi.getAudit(accountId() ?? ""),
    enabled: Boolean(accountId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  }));
}

/** Which OAuth providers (Gmail/Outlook) the server can connect. */
export function useMailOAuthProviders() {
  return createSafeQuery(() => ({
    queryKey: mailKeys.oauthProviders(),
    queryFn: () => mailApi.oauthProviders(),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  }));
}

/**
 * Kick off the OAuth consent flow: ask the server for the authorize URL (which
 * also plants the HttpOnly flow cookie) and send the whole browser there. The
 * provider redirects back to `/mail/settings?oauth=…` when done.
 */
export function useStartMailOAuth(): UseMutationResult<MailOAuthStart, Error, MailOAuthProvider> {
  return createMutation(() => ({
    mutationFn: (provider: MailOAuthProvider) => mailApi.oauthStart(provider),
    onSuccess: (res) => {
      globalThis.location.assign(res.url);
    },
  }));
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useConnectAccount(): UseMutationResult<
  MailAccountView,
  Error,
  ConnectAccountInput
> {
  return createMutation(() => ({
    mutationFn: (body: ConnectAccountInput) => mailApi.connectAccount(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailKeys.accounts() });
    },
  }));
}

export function useDeleteAccount(): UseMutationResult<void, Error, string> {
  return createMutation(() => ({
    mutationFn: (id: string) => mailApi.deleteAccount(id),
    onSuccess: (_data, id) => {
      void mailMirror.forget(id);
      void queryClient.invalidateQueries({ queryKey: mailKeys.accounts() });
    },
  }));
}

export function useSyncAccount(): UseMutationResult<SyncResult[], Error, string> {
  return createMutation(() => ({
    mutationFn: (id: string) => mailApi.syncAccount(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: mailKeys.folders(id) });
      void queryClient.invalidateQueries({ queryKey: mailKeys.messagesByAccount(id) });
      void queryClient.invalidateQueries({ queryKey: mailKeys.threadsByAccount(id) });
    },
  }));
}

interface SendVars {
  accountId: string;
  body: SendMessageInput;
}

/**
 * Sends a message. Offline, the send is queued in the mirror's outbox (resolves
 * as success — it will be replayed on reconnect) instead of erroring.
 */
export function useSendMessage(): UseMutationResult<void, Error, SendVars> {
  return createMutation(() => ({
    mutationFn: async ({ accountId, body }: SendVars) => {
      await mailMirror.sendOrQueue(accountId, body);
    },
    onSuccess: (_data, { accountId }) => {
      void queryClient.invalidateQueries({ queryKey: mailKeys.messagesByAccount(accountId) });
    },
  }));
}

/** Saves a draft, queuing it in the mirror's outbox when offline. */
export function useSaveDraft(): UseMutationResult<void, Error, SendVars> {
  return createMutation(() => ({
    mutationFn: async ({ accountId, body }: SendVars) => {
      await mailMirror.draftOrQueue(accountId, body);
    },
  }));
}

interface FlagVars {
  accountId: string;
  uid: number;
  folder: string;
  body: FlagsInput;
}

/**
 * Toggles flags with an optimistic patch of the cached message list + detail, so
 * a star/read toggle feels instant. On error the snapshot is restored.
 */
export function useSetFlags(): UseMutationResult<
  void,
  Error,
  FlagVars,
  { prevList?: MessageSummary[] }
> {
  return createMutation(() => ({
    mutationFn: ({ accountId, uid, body }: FlagVars) => mailApi.setFlags(accountId, uid, body),
    onMutate: ({ accountId, uid, folder, body }: FlagVars) => {
      const key = mailKeys.messages(accountId, folder);
      const prevList = queryClient.getQueryData<MessageSummary[]>(key);
      const add = new Set(body.add ?? []);
      const remove = new Set(body.remove ?? []);
      const patch = (m: MessageSummary): MessageSummary => {
        if (m.uid !== uid) {
          return m;
        }
        let { seen, flagged } = m;
        if (add.has(IMAP_FLAG_SEEN)) {
          seen = true;
        }
        if (remove.has(IMAP_FLAG_SEEN)) {
          seen = false;
        }
        if (add.has(IMAP_FLAG_FLAGGED)) {
          flagged = true;
        }
        if (remove.has(IMAP_FLAG_FLAGGED)) {
          flagged = false;
        }
        return { ...m, seen, flagged };
      };
      if (prevList) {
        queryClient.setQueryData<MessageSummary[]>(key, prevList.map(patch));
      }
      // Persist the toggle into the offline mirror so it survives a reload.
      const flagPatch: { seen?: boolean; flagged?: boolean } = {};
      if (add.has(IMAP_FLAG_SEEN)) {
        flagPatch.seen = true;
      }
      if (remove.has(IMAP_FLAG_SEEN)) {
        flagPatch.seen = false;
      }
      if (add.has(IMAP_FLAG_FLAGGED)) {
        flagPatch.flagged = true;
      }
      if (remove.has(IMAP_FLAG_FLAGGED)) {
        flagPatch.flagged = false;
      }
      void mailMirror.recordFlags(accountId, folder, uid, flagPatch);
      return { prevList };
    },
    onError: (_err, { accountId, folder }, context) => {
      if (context?.prevList) {
        queryClient.setQueryData(mailKeys.messages(accountId, folder), context.prevList);
      }
    },
    onSettled: (_data, _err, { accountId, folder }) => {
      void queryClient.invalidateQueries({ queryKey: mailKeys.messages(accountId, folder) });
      void queryClient.invalidateQueries({ queryKey: mailKeys.threads(accountId, folder) });
    },
  }));
}

interface MoveVars {
  accountId: string;
  uid: number;
  body: MoveInput;
}

export function useMoveMessage(): UseMutationResult<void, Error, MoveVars> {
  return createMutation(() => ({
    mutationFn: ({ accountId, uid, body }: MoveVars) => mailApi.move(accountId, uid, body),
    onSuccess: (_data, { accountId, uid, body }) => {
      void mailMirror.recordMove(accountId, body.folder, uid);
      void queryClient.invalidateQueries({ queryKey: mailKeys.messagesByAccount(accountId) });
      void queryClient.invalidateQueries({ queryKey: mailKeys.threadsByAccount(accountId) });
      void queryClient.invalidateQueries({ queryKey: mailKeys.folders(accountId) });
    },
  }));
}

interface SaveRulesVars {
  accountId: string;
  rules: MailRule[];
}

export function useSaveRules(): UseMutationResult<MailRule[], Error, SaveRulesVars> {
  return createMutation(() => ({
    mutationFn: ({ accountId, rules }: SaveRulesVars) => mailApi.putRules(accountId, rules),
    onSuccess: (data, { accountId }) => {
      queryClient.setQueryData(mailKeys.rules(accountId), data);
    },
  }));
}
